// Årsfristene: skattemelding, årsregnskap og aksjonærregisteroppgaven.
//
// Én kilde, fordi tre steder trenger de samme datoene og ville sagt forskjellige
// ting om samme foretak på samme dag:
//   1. hjem-dashbordets fristliste (`deriveDashboardDeadlines`),
//   2. AI Regnskapsførerens fristagent og påminnelsesrad,
//   3. månedsfargen i månedsvelgeren (`apMonthStatuses`).
//
// MVA er BEVISST ikke med. MVA-fristene følger foretakets termin og eies av
// `plummeVatDueDate` i backend.jsx — den brukes allerede av både innsendinger,
// periodegjennomgangen og månedsfargen. To fristtabeller for MVA er nettopp det
// denne fila finnes for å unngå.
//
// ------------------------------------------------------------- inntektsår
//
// Fristen ligger i ÅRET ETTER inntektsåret. Skattemeldingen for 2025 forfaller
// 31. mai 2026. Det er hele grunnen til at `dueInMonth` under må gå gjennom to
// inntektsår for å svare på «hvilke frister forfaller i mai 2026»: svaret ligger
// i 2025, ikke i 2026.
//
// ---------------------------------------------------------------- kilder
//
// Skattemelding for næringsdrivende: 31. mai (NO-TAX-BUSINESS-RETURN).
// Årsregnskap til Regnskapsregisteret: 31. juli (NO-LOVDATA-ACCOUNTING-ACT).
// Aksjonærregisteroppgaven (RF-1086): 31. januar, jf.
// skatteforvaltningsforskriften § 7-7-4 første ledd
// (NO-LOVDATA-TAX-ADMINISTRATION-REGULATION, NO-TAX-SHAREHOLDER-REGISTER-STATEMENT).
(() => {
  const text = (value) => (typeof value === 'string' ? value : '');
  const yearKey = (year) => String(Number(year));

  // Selskapsformen leses gjennom den delte klassifiseringen, ikke gjennom et
  // lokalt `AS|ASA`-regex. `annualAccountsApplicability: 'applicable'` er sann
  // for NO AS og ASA og ingen andre — samme utvalg som regnskapsplikten.
  const scopeFor = (state, incomeYear) =>
    globalThis.PlummeAccountingScope?.resolveAccountingScope?.(state?.settings || {}, incomeYear) || null;

  const isNorwegianLimitedCompany = (scope) =>
    scope?.jurisdiction === 'NO' && ['AS', 'ASA'].includes(text(scope?.legalFormCode));

  // Levert-flaggene. `deadlinesByYear` skrives av innsendingene og av
  // regnskapsimporten; prosess-flaggene av årsavslutningen. Begge veier teller,
  // ellers ville et regnskap som ble levert utenfor Plumme stått som uoppgjort
  // for alltid.
  const submittedFlags = (state, incomeYear) => {
    const key = yearKey(incomeYear);
    const deadlines = state?.deadlinesByYear?.[key] || {};
    const closing = state?.yearClosingProcessByYear?.[key] || {};
    return {
      taxSubmitted: Boolean(
        deadlines.taxSubmitted ||
        deadlines.skattemeldingSubmitted ||
        state?.taxReturnProcessByYear?.[key]?.submittedAt ||
        closing.taxMarkedOutsidePlumme),
      annualAccountsSubmitted: Boolean(
        deadlines.annualAccountsSubmitted ||
        state?.annualAccountsProcessByYear?.[key]?.submittedAt ||
        closing.annualAccountsMarkedOutsidePlumme),
      // Aksjonærregisteroppgaven har ingen modul i Plumme. Den kan bare bli
      // levert utenfor, og markeres derfor manuelt — se `mark-annual-deadline-filed`
      // i functions/autopilot/autopilotAnnualDeadline.ts.
      shareholderRegisterSubmitted: Boolean(
        deadlines.shareholderRegisterSubmitted ||
        closing.shareholderRegisterMarkedOutsidePlumme),
    };
  };

  // Katalogen. `month` er 0-indeksert, `day` er fristdagen i året ETTER
  // inntektsåret.
  const ANNUAL_DEADLINE_RULES = Object.freeze([
    {
      id: 'shareholderRegisterSubmitted',
      name: 'Aksjonærregisteroppgaven',
      month: 0,
      day: 31,
      // Ingen innsendingsmodul finnes. Raden er en påminnelse, og peker til
      // årsavslutningen der statusen følges opp manuelt.
      route: 'arsavslutning',
      // Speiler `MARKABLE_ANNUAL_DEADLINES` i
      // functions/autopilot/autopilotAnnualDeadline.ts, som er den kanoniske
      // datoen. Nettleseren kan ikke importere ESM derfra — endres den ene, må
      // den andre endres i samme slengen.
      ruleId: 'NO.FILING.SHAREHOLDER_REGISTER.DEADLINE.V1',
      sourceIds: ['NO-LOVDATA-TAX-ADMINISTRATION-REGULATION', 'NO-TAX-SHAREHOLDER-REGISTER-STATEMENT'],
      applies: (scope) => isNorwegianLimitedCompany(scope),
      // Boligaksjeselskap er unntatt, og selskaper der Euronext VPS leverer på
      // vegne av selskapet skal ikke levere selv. Plumme kjenner ingen av delene,
      // så raden er en påminnelse — ikke en påstand om at foretaket har plikten.
      qualified: true,
    },
    {
      id: 'taxSubmitted',
      name: 'Skattemelding',
      month: 4,
      day: 31,
      route: 'skattemelding',
      ruleId: 'NO.FILING.TAX_RETURN.DEADLINE.V1',
      sourceIds: ['NO-TAX-BUSINESS-RETURN'],
      applies: () => true,
      qualified: false,
    },
    {
      id: 'annualAccountsSubmitted',
      name: 'Årsregnskap',
      month: 6,
      day: 31,
      route: 'arsregnskap',
      ruleId: 'NO.FILING.ANNUAL_ACCOUNTS.DEADLINE.V1',
      sourceIds: ['NO-LOVDATA-ACCOUNTING-ACT'],
      applies: (scope) => scope?.annualAccountsApplicability === 'applicable',
      qualified: false,
    },
  ]);

  // Fristene for ett inntektsår, i forfallsrekkefølge.
  const annualDeadlinesForIncomeYear = (state, incomeYear) => {
    const year = Number(incomeYear);
    if (!Number.isFinite(year)) return [];
    const scope = scopeFor(state, year);
    const submitted = submittedFlags(state, year);
    return ANNUAL_DEADLINE_RULES
      .filter((rule) => rule.applies(scope))
      .map((rule) => {
        const taxDue = rule.id === 'taxSubmitted'
          ? globalThis.PlummeNorwegianBusinessDeadline.taxReturnDeadline(year)
          : null;
        return ({
        id: rule.id,
        name: rule.name,
        incomeYear: year,
        dueDate: taxDue
          ? new Date(taxDue.getUTCFullYear(), taxDue.getUTCMonth(), taxDue.getUTCDate())
          : new Date(year + 1, rule.month, rule.day),
        submitted: Boolean(submitted[rule.id]),
        route: rule.route,
        ruleId: rule.ruleId,
        sourceIds: rule.sourceIds,
        qualified: Boolean(rule.qualified),
        });
      })
      .sort((a, b) => a.dueDate - b.dueDate);
  };

  // Fristene som forfaller i en gitt KALENDERMÅNED. Går gjennom både forrige og
  // dette inntektsåret: en frist i januar 2026 tilhører inntektsåret 2025, og en
  // ren `incomeYear === calendarYear`-oppslag ville aldri funnet den.
  const annualDeadlinesDueInMonth = (state, calendarYear, monthIndex) => {
    const year = Number(calendarYear);
    const month = Number(monthIndex);
    if (!Number.isFinite(year) || !Number.isFinite(month)) return [];
    return [year - 2, year - 1]
      .flatMap((incomeYear) => annualDeadlinesForIncomeYear(state, incomeYear))
      .filter((entry) => entry.dueDate.getFullYear() === year && entry.dueDate.getMonth() === month)
      .sort((a, b) => a.dueDate - b.dueDate);
  };

  // Frister i dette kalenderåret som er passert uten at plikten er gjort opp,
  // per måned. Speiler `apMissedVatDeadlines` i autopilot-periods.jsx.
  const missedAnnualDeadlinesByMonth = (state, calendarYear, today = new Date()) => {
    const missed = new Array(12).fill(false);
    for (let month = 0; month < 12; month += 1) {
      const overdue = annualDeadlinesDueInMonth(state, calendarYear, month)
        .some((entry) => !entry.submitted && entry.dueDate < today);
      if (overdue) missed[month] = true;
    }
    return missed;
  };

  globalThis.PlummeAnnualDeadlines = Object.freeze({
    ANNUAL_DEADLINE_RULES,
    annualDeadlinesForIncomeYear,
    annualDeadlinesDueInMonth,
    missedAnnualDeadlinesByMonth,
  });
})();
